import { useRef, useState } from 'react';
import { decisionSetApi, type PublishResult } from '../services/api';

/**
 * Publish a decision set (KB 172 §5).
 *
 * This lives on the dashboard's /viz route, which is behind MSAL — so the control is authenticated
 * by construction, while the static /viz/*.html pages stay anonymous. That is the whole auth split
 * made visible: you read a decision on an open link, and you change one from inside the app.
 *
 * It accepts JSON only, on purpose. The renderer pages already import JSON, CSV, and XLSX and
 * export JSON, so formats enter through the renderer — where those parsers already live and are
 * tested — and leave as a decision set. Re-implementing three parsers here to save one "Export
 * JSON" click would buy a second place for them to disagree.
 */

/*
 * A note on the classes below: this Tailwind config maps every semantic colour to a bare
 * `var(--dt-*)`, and Tailwind cannot apply an opacity modifier to one — `bg-error/5` compiles to
 * nothing at all and `border-error/40` leaves the border at `currentColor`. So the states here use
 * the solid `*-bg` tokens the design system already ships. Check the built CSS, not the screenshot:
 * a tinted box that inherits the text colour can look deliberate while carrying no signal.
 */

const SLUG = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

interface Loaded {
  doc: any;
  filename: string;
  decided: number;
}

/** Catch the wrong-file case immediately; the server remains the authority on everything else. */
function readSet(text: string, filename: string): Loaded {
  let doc: any;
  try {
    doc = JSON.parse(text);
  } catch (e) {
    throw new Error(`${filename} is not valid JSON — ${(e as Error).message}`);
  }
  if (doc?.kind !== 'decision-set') {
    throw new Error(
      `${filename} is not a decision set (its "kind" is ${JSON.stringify(doc?.kind) ?? 'missing'}). ` +
        'Use the "Export JSON" button on a decision table page.',
    );
  }
  if (!doc.id || !SLUG.test(doc.id)) {
    throw new Error(`"id" must be a lowercase slug — got ${JSON.stringify(doc.id)}`);
  }
  if (!Array.isArray(doc.rows) || !doc.rows.length) throw new Error('the set has no rows');
  if (!Array.isArray(doc.options) || !doc.options.length) throw new Error('the set has no options');

  const missing = doc.rows.findIndex((r: any) => r?.[doc.idField] === undefined);
  if (missing >= 0) throw new Error(`row ${missing} has no "${doc.idField}" — it would render blank`);

  const decided = Object.values(doc.choices || {}).filter((c) => c !== 'keep').length;
  return { doc, filename, decided };
}

export function PublishDecisionSet({ onPublished }: { onPublished?: () => void }) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [visibility, setVisibility] = useState<'public' | 'tenant'>('public');
  const [changeNote, setChangeNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<string[]>([]);
  const [result, setResult] = useState<PublishResult | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  function reset() {
    setError(null);
    setDetails([]);
    setResult(null);
  }

  async function take(file: File | undefined) {
    reset();
    setLoaded(null);
    if (!file) return;
    try {
      setLoaded(readSet(await file.text(), file.name));
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function publish() {
    if (!loaded) return;
    reset();
    setBusy(true);
    try {
      setResult(await decisionSetApi.publish(loaded.doc, { visibility, changeNote: changeNote.trim() }));
      setLoaded(null);
      setChangeNote('');
      onPublished?.();
    } catch (e) {
      const msg = (e as Error).message;
      // `put` throws "API <status>: <body>"; the body is where the write gate explains itself.
      const status = /^API (\d+): /.exec(msg)?.[1];
      let body: any = null;
      try {
        body = JSON.parse(msg.replace(/^API \d+: /, ''));
      } catch { /* not JSON — show the raw message */ }

      if (status === '401' || status === '403') {
        setError('Publishing needs a signed-in session. Reload the page to sign in, then try again.');
      } else if (body?.details?.length) {
        // The endpoint returns every problem at once; showing only the first would make fixing
        // a spreadsheet a one-error-per-round-trip exercise.
        setError(body.error || 'The decision set was rejected');
        setDetails(body.details.map((d: any) => (d.path ? `${d.path}: ${d.message}` : d.message)));
      } else {
        setError(body?.error || msg);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-6 bg-tower-surface border border-tower-border rounded-lg">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
      >
        <span className="text-sm font-semibold text-tower-accent">Publish a decision set</span>
        <span className="text-xs text-tower-muted">
          — drop an exported <span className="font-mono">.json</span>; it gets a permanent link and a version
        </span>
        <div className="flex-1" />
        <span className="text-tower-muted text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-tower-border pt-4">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); take(e.dataTransfer.files?.[0]); }}
            onClick={() => fileInput.current?.click()}
            className={`border border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              dragging ? 'border-tower-accent bg-tower-surface' : 'border-tower-border hover:border-tower-accent'
            }`}
          >
            <p className="text-sm text-tower-text">Drop a decision set JSON file here, or click to choose one</p>
            <p className="text-xs text-tower-muted mt-1">
              Export one from any decision table page. CSV and Excel are imported on that page —
              publish the JSON it exports.
            </p>
            <input
              ref={fileInput}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={(e) => take(e.target.files?.[0])}
            />
          </div>

          {error && (
            <div className="mt-3 p-3 border border-error bg-error-bg rounded text-sm text-tower-text">
              <p className="font-semibold">{error}</p>
              {details.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {details.map((d, i) => (
                    <li key={i} className="text-xs font-mono text-tower-muted">• {d}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {loaded && (
            <div className="mt-3 p-3 border border-tower-border rounded">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-sm font-semibold text-tower-text">{loaded.doc.title}</span>
                <span className="text-xs font-mono text-tower-muted">{loaded.doc.id}</span>
                <span className="text-xs text-tower-muted">
                  {loaded.doc.rows.length} rows · {loaded.doc.options.length} options ·{' '}
                  {loaded.decided ? `${loaded.decided} decided` : 'no choices yet'} · {loaded.doc.status ?? 'draft'}
                </span>
              </div>
              <p className="text-[11px] text-tower-muted mt-1">from {loaded.filename}</p>

              <div className="flex gap-3 mt-3 items-center flex-wrap">
                <label className="text-xs text-tower-muted flex items-center gap-1.5">
                  Visibility
                  <select
                    value={visibility}
                    onChange={(e) => setVisibility(e.target.value as 'public' | 'tenant')}
                    className="bg-tower-bg border border-tower-border rounded px-2 py-1 text-xs text-tower-text"
                  >
                    <option value="public">public — anyone with the link</option>
                    <option value="tenant">tenant — only your team</option>
                  </select>
                </label>
                <input
                  value={changeNote}
                  onChange={(e) => setChangeNote(e.target.value)}
                  placeholder="What changed? (optional)"
                  className="flex-1 min-w-[12rem] bg-tower-bg border border-tower-border rounded px-2 py-1 text-xs text-tower-text"
                />
                <button
                  onClick={publish}
                  disabled={busy}
                  className="px-3 py-1.5 text-xs font-semibold rounded bg-tower-bg text-tower-accent border border-tower-accent hover:bg-tower-border disabled:opacity-50"
                >
                  {busy ? 'Publishing…' : 'Publish'}
                </button>
              </div>
            </div>
          )}

          {result && (
            <div className="mt-3 p-3 border border-success bg-success-bg rounded text-sm">
              <p className="text-tower-text">
                Published <span className="font-semibold">{result.title}</span> as version{' '}
                <span className="font-mono">{result.version}</span>
                {result.supersededVersion !== null && (
                  <span className="text-tower-muted"> — version {result.supersededVersion} is kept and still readable</span>
                )}
              </p>
              <a
                href={result.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-mono text-tower-accent hover:underline"
              >
                {result.href} →
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
